/**
 * Pure label logic for the app-bar billing status.
 *
 * Kept separate from the component so the rules below are unit-testable
 * without mounting React Query: what an org sees about its own standing
 * is the thing most likely to be quietly wrong, and it is read on every
 * page.
 */

import { formatCredits } from "@/features/billing/formatCredits";

export type BillingStatusInput = {
  /** Total balance in micros (both buckets). */
  balanceMicros: number | undefined;
  /** Micros per credit, from the me payload. */
  microsPerCredit: number;
  /** Plan name of the live agreement, or null when there is none. */
  planName: string | null;
  /** The server's own answer to "is this org blocked?" — it already
   *  folds in the global flag, the per-org override and the negative
   *  cap, none of which the SPA should try to re-derive. */
  blocked: boolean;
  /** Whether a balance running out will actually stop work. When
   *  enforcement is off, a low balance is a fact, not a warning. */
  enforced: boolean;
  /** True while either query is still in flight. */
  loading: boolean;
  /** Intl tag, normally intlLocale(). */
  locale: string;
};

export type BillingStatus = {
  /** Rendered credit count, already localised to a plain integer. */
  credits: number;
  /** Short label: "1 240" or "1 240 · Pro". */
  label: string;
  /** Drives the chip colour. "empty" is out of credits, "low" is under
   *  one thousand, "ok" otherwise. */
  tone: "ok" | "low" | "empty";
  /** False while loading — the caller renders nothing rather than a
   *  flash of "0", which reads as "you are out of credits". */
  show: boolean;
};

/** Under this many credits the chip warns. One thousand is the smallest
 *  purchasable pack, so it is the point where topping up is the obvious
 *  next action rather than a premature nag. */
export const LOW_CREDIT_THRESHOLD = 1_000;

export function resolveBillingStatus(input: BillingStatusInput): BillingStatus {
  const { balanceMicros, microsPerCredit, planName, blocked, enforced, loading } =
    input;
  // Guard the divisor: a zero or missing micros_per_credit would yield
  // Infinity/NaN and render as garbage in the app bar.
  const per = microsPerCredit > 0 ? microsPerCredit : 10_000;
  const credits = Math.floor((balanceMicros ?? 0) / per);
  // Colour follows CONSEQUENCE, not arithmetic. A deployment that only
  // tracks credits (enforcement off) never stops a turn, so painting a
  // zero balance red there tells an operator their work is about to
  // stop when nothing will — and the operator's own root org, which
  // never buys credits, sits at zero permanently. Red is reserved for
  // the server saying this org is actually blocked.
  const tone: BillingStatus["tone"] = blocked
    ? "empty"
    : enforced && credits < LOW_CREDIT_THRESHOLD
      ? "low"
      : "ok";
  // Grouped so 1240 reads as "1,240" / "1 240" rather than a wall of
  // digits — following the UI LANGUAGE, not the browser's locale. A
  // Swedish page showing the chip as "14,000" put an English separator
  // next to its own "14 000" on the billing card.
  const count = formatCredits(credits, { locale: input.locale });
  return {
    credits,
    label: planName ? `${count} · ${planName}` : count,
    tone,
    // Never render mid-flight: balanceMicros is undefined until the
    // query settles, and showing "0" then tells a paying customer they
    // are out of credits on every page load.
    show: !loading && balanceMicros !== undefined,
  };
}
